import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { organizationsAPI } from '../lib/api';
import { useNotification } from '../contexts/NotificationContext';
import {
  Building2, Users, Mail, UserPlus, Shield, Trash2, Crown,
  Eye, Edit3, AlertTriangle, Check, X, Loader2, Copy, ArrowLeft,
  Settings, Send, Clock
} from 'lucide-react';

const OrganizationSettings = () => {
  const navigate = useNavigate();
  const { currentOrganization, refreshUserData } = useAuth();
  const { notify } = useNotification();
  
  const [loading, setLoading] = useState(true);
  const [members, setMembers] = useState([]);
  const [pendingInvites, setPendingInvites] = useState([]);
  const [orgDetails, setOrgDetails] = useState(null);
  
  // Modal states
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: '', role: 'member' });
  const [addMemberForm, setAddMemberForm] = useState({ email: '', password: '', firstName: '', lastName: '', role: 'member' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (currentOrganization?.id) {
      fetchOrgData();
    }
  }, [currentOrganization]);

  const fetchOrgData = async () => {
    setLoading(true);
    try {
      const [orgRes, membersRes, invitesRes] = await Promise.all([
        organizationsAPI.get(currentOrganization.id),
        organizationsAPI.getMembers(currentOrganization.id),
        organizationsAPI.getInvites(currentOrganization.id).catch(() => ({ data: [] }))
      ]);
      
      setOrgDetails(orgRes.data);
      setMembers(membersRes.data);
      setPendingInvites(invitesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch org data:', error);
      notify.error('Failed to load organization data');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteMember = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await organizationsAPI.inviteMember(currentOrganization.id, inviteForm);
      notify.success(`Invitation sent to ${inviteForm.email}`);
      
      // Copy invite URL to clipboard
      const inviteUrl = `${window.location.origin}${res.data.inviteUrl}`;
      navigator.clipboard.writeText(inviteUrl);
      notify.info('Invite link copied to clipboard');
      
      setShowInviteModal(false);
      setInviteForm({ email: '', role: 'member' });
      fetchOrgData();
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Failed to send invitation');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await organizationsAPI.addMember(currentOrganization.id, addMemberForm);
      notify.success(`${addMemberForm.email} added to organization`);
      setShowAddMemberModal(false);
      setAddMemberForm({ email: '', password: '', firstName: '', lastName: '', role: 'member' });
      fetchOrgData();
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Failed to add member');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await organizationsAPI.updateMemberRole(currentOrganization.id, userId, { role: newRole });
      notify.success('Role updated');
      fetchOrgData();
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleRemoveMember = async (userId, email) => {
    if (!window.confirm(`Remove ${email} from the organization?`)) return;
    
    try {
      await organizationsAPI.removeMember(currentOrganization.id, userId);
      notify.success('Member removed');
      fetchOrgData();
    } catch (error) {
      notify.error(error.response?.data?.detail || 'Failed to remove member');
    }
  };

  const handleCancelInvite = async (inviteId) => {
    try {
      await organizationsAPI.cancelInvite(currentOrganization.id, inviteId);
      notify.success('Invitation cancelled');
      fetchOrgData();
    } catch (error) {
      notify.error('Failed to cancel invitation');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner': return <Crown className="w-4 h-4 text-amber-400" />;
      case 'admin': return <Shield className="w-4 h-4 text-violet-400" />;
      case 'member': return <Edit3 className="w-4 h-4 text-blue-400" />;
      case 'viewer': return <Eye className="w-4 h-4 text-slate-400" />;
      default: return <Users className="w-4 h-4 text-slate-400" />;
    }
  };

  const canManageMembers = currentOrganization?.role === 'owner' || currentOrganization?.role === 'admin';

  if (!currentOrganization) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl text-white mb-2">No Organization Selected</h2>
          <p className="text-slate-400 mb-4">Create or join an organization to get started.</p>
          <button
            onClick={() => navigate('/organizations/new')}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700"
          >
            Create Organization
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-violet-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-900 to-violet-950">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-violet-500/20">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">{orgDetails?.name}</h1>
                  <p className="text-violet-300 text-sm flex items-center gap-2">
                    {getRoleIcon(currentOrganization.role)}
                    <span className="capitalize">{currentOrganization.role}</span>
                    <span className="text-slate-500">•</span>
                    <span>{members.length} members</span>
                  </p>
                </div>
              </div>
            </div>
            
            {canManageMembers && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Invite via Email
                </button>
                <button
                  onClick={() => setShowAddMemberModal(true)}
                  className="flex items-center gap-2 px-4 py-2 border border-violet-500/50 text-violet-300 hover:bg-violet-500/10 rounded-lg transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Add Member
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Members Section */}
        <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-slate-700">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Users className="w-5 h-5 text-violet-400" />
              Members ({members.length})
            </h2>
          </div>
          
          <div className="divide-y divide-slate-700">
            {members.map((member) => (
              <div key={member.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-slate-700 rounded-full flex items-center justify-center">
                    <span className="text-white font-medium">
                      {member.firstName?.[0] || member.email[0].toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="text-white font-medium">
                      {member.firstName} {member.lastName}
                      {member.userId === currentOrganization?.userId && (
                        <span className="text-xs text-slate-500 ml-2">(you)</span>
                      )}
                    </p>
                    <p className="text-slate-400 text-sm">{member.email}</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 px-3 py-1 bg-slate-700/50 rounded-full">
                    {getRoleIcon(member.role)}
                    <span className="text-sm text-slate-300 capitalize">{member.role}</span>
                  </div>
                  
                  {canManageMembers && member.role !== 'owner' && (
                    <div className="flex items-center gap-2">
                      <select
                        value={member.role}
                        onChange={(e) => handleUpdateRole(member.userId, e.target.value)}
                        className="bg-slate-700 border border-slate-600 text-slate-300 text-sm rounded-lg px-3 py-1.5 focus:ring-violet-500 focus:border-violet-500"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="member">Member</option>
                        <option value="admin">Admin</option>
                        {currentOrganization.role === 'owner' && (
                          <option value="owner">Owner</option>
                        )}
                      </select>
                      <button
                        onClick={() => handleRemoveMember(member.userId, member.email)}
                        className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Invites Section */}
        {canManageMembers && pendingInvites.length > 0 && (
          <div className="bg-slate-800/50 rounded-2xl border border-slate-700 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Clock className="w-5 h-5 text-amber-400" />
                Pending Invitations ({pendingInvites.length})
              </h2>
            </div>
            
            <div className="divide-y divide-slate-700">
              {pendingInvites.map((invite) => (
                <div key={invite.id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-amber-500/20 rounded-full flex items-center justify-center">
                      <Mail className="w-5 h-5 text-amber-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{invite.email}</p>
                      <p className="text-slate-400 text-sm capitalize">
                        Invited as {invite.role}
                      </p>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleCancelInvite(invite.id)}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Role Descriptions */}
        <div className="mt-8 bg-slate-800/30 rounded-xl p-6 border border-slate-700/50">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Role Permissions</h3>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-start gap-3">
              <Crown className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Owner</p>
                <p className="text-slate-400 text-sm">Full control, can delete org</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-violet-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Admin</p>
                <p className="text-slate-400 text-sm">Manage members, all data access</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Edit3 className="w-5 h-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Member</p>
                <p className="text-slate-400 text-sm">Create & edit assessments</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-slate-400 mt-0.5" />
              <div>
                <p className="text-white font-medium">Viewer</p>
                <p className="text-slate-400 text-sm">Read-only access</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Mail className="w-5 h-5 text-violet-400" />
                Invite Team Member
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Send an email invitation to join {currentOrganization?.name}
              </p>
            </div>
            
            <form onSubmit={handleInviteMember} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="colleague@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                <select
                  value={inviteForm.role}
                  onChange={(e) => setInviteForm({ ...inviteForm, role: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="viewer">Viewer - Read-only access</option>
                  <option value="member">Member - Create & edit</option>
                  <option value="admin">Admin - Manage members</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl w-full max-w-md border border-slate-700">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-violet-400" />
                Add Team Member
              </h2>
              <p className="text-slate-400 text-sm mt-1">
                Create an account and add them directly
              </p>
            </div>
            
            <form onSubmit={handleAddMember} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                  <input
                    type="text"
                    value={addMemberForm.firstName}
                    onChange={(e) => setAddMemberForm({ ...addMemberForm, firstName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="John"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                  <input
                    type="text"
                    value={addMemberForm.lastName}
                    onChange={(e) => setAddMemberForm({ ...addMemberForm, lastName: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    placeholder="Doe"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                <input
                  type="email"
                  required
                  value={addMemberForm.email}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, email: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="john@example.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Temporary Password</label>
                <input
                  type="text"
                  required
                  value={addMemberForm.password}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, password: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  placeholder="Temporary password"
                />
                <p className="text-xs text-slate-500 mt-1">Share this with the user so they can log in</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Role</label>
                <select
                  value={addMemberForm.role}
                  onChange={(e) => setAddMemberForm({ ...addMemberForm, role: e.target.value })}
                  className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                >
                  <option value="viewer">Viewer - Read-only access</option>
                  <option value="member">Member - Create & edit</option>
                  <option value="admin">Admin - Manage members</option>
                </select>
              </div>
              
              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddMemberModal(false)}
                  className="px-4 py-2 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex items-center gap-2 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg transition-colors disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
                  Add Member
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationSettings;
